/**
 * Single source of truth for interpreter-queue language defaults.
 *
 * The queue-service match is strict: an interpreter is only eligible for a
 * request when their advertised languages include the request's language
 * (vrs-server/src/lib/queue-service.ts findBestMatch). Clients request
 * DEFAULT_REQUEST_LANGUAGE, so every surface that marks an interpreter
 * available MUST advertise a language list that contains it — the prejoin
 * screen once hardcoded [ 'en', 'es' ], which silently made interpreters
 * unmatchable for ASL requests while looking "Active" in the UI.
 */
import { DEFAULT_REQUEST_LANGUAGE, defaultRequestLanguage, requestLanguageChoices, scheduleLanguageChoices, selectRequestLanguage } from '../../../shared/service-regions';
import { getWhitelabelConfig } from '../base/whitelabel/functions';
export { DEFAULT_REQUEST_LANGUAGE };
export const DEFAULT_INTERPRETER_LANGUAGES = [DEFAULT_REQUEST_LANGUAGE, 'en', 'es'];
/**
 * The language a client on this tenant requests when they have not chosen one.
 *
 * ASL is the right default for Malka and Maple, and the wrong one for MalkaVCS:
 * its interpreters sign Kuwaiti, Saudi, or pan-Arab sign language, and because
 * the match is exact-string an ASL request would sit in the queue forever
 * against a room full of available interpreters. Tenants declare their own via
 * `operations.defaultRequestLanguage`. An account granted another region gets
 * that region's default there.
 *
 * @param {string|null} [regionId] - The region chosen by a multi-region account.
 * @returns {string} The atom to request.
 */
export function getDefaultRequestLanguage(regionId) {
    return defaultRequestLanguage(getWhitelabelConfig()?.operations, regionId);
}
/**
 * The sign languages a client on this tenant can request for `service`: the
 * entries of `operations.interpreterPools` that are sign languages of the
 * tenant's service region, with the same table and atoms as the web client
 * (shared/service-regions.js); in another region the account is granted, that
 * region's own list. The list is never empty.
 *
 * @param {RequestService} service - Which pool to read ('vri' or 'vrs').
 * @param {string|null} [regionId] - The region chosen by a multi-region account.
 * @returns {RequestLanguageChoice[]} The pool's sign languages, in pool order.
 */
export function getRequestLanguageChoices(service, regionId) {
    return requestLanguageChoices(getWhitelabelConfig()?.operations, service, regionId);
}
/**
 * The language a request for `service` sends: `saved` while this tenant's pool
 * still offers it, else the tenant default, else the pool's first language.
 *
 * @param {RequestService} service - Which pool to read ('vri' or 'vrs').
 * @param {string|null} [saved] - The client's saved pick, if any.
 * @param {string|null} [regionId] - The region chosen by a multi-region account.
 * @returns {string} The atom to send.
 */
export function getRequestLanguage(service, saved, regionId) {
    return selectRequestLanguage(getWhitelabelConfig()?.operations, service, saved, regionId);
}
/**
 * The languages a VRI session on this tenant can be scheduled in: the sign
 * languages a live VRI request offers, then the region's spoken languages
 * (French and Spanish in the US and Canada, labelled "(spoken)").
 *
 * @param {string|null} [regionId] - The region chosen by a multi-region account.
 * @returns {RequestLanguageChoice[]} Schedulable languages, sign languages first.
 */
export function getScheduleLanguageChoices(regionId) {
    return scheduleLanguageChoices(getWhitelabelConfig()?.operations, regionId);
}
/**
 * The languages an interpreter on this tenant advertises when their profile
 * carries none. Always contains the tenant's default request language, or the
 * tenant's own interpreters would be unmatchable by its own clients.
 */
export function getDefaultInterpreterLanguages() {
    const requestLanguage = getDefaultRequestLanguage();
    if (requestLanguage === DEFAULT_REQUEST_LANGUAGE) {
        return DEFAULT_INTERPRETER_LANGUAGES;
    }
    const declared = getWhitelabelConfig()?.operations?.defaultInterpreterLanguages;
    const configured = Array.isArray(declared)
        ? declared.filter((language) => typeof language === 'string')
        : [];
    return configured.includes(requestLanguage) ? configured : [requestLanguage, ...configured];
}
